/**
 * Scopes every page can rely on: they are loaded at app start or by the
 * layout, so a key of theirs never needs an on-demand load.
 */
const GLOBAL_SCOPE_ALIASES = new Set(['common', 'codext', 'page', 'group']);

/**
 * Transloco stores a scope under its camelCased alias, and `toCamelCase`
 * drops the underscore: the scope `p_vho` owns the keys `pvho.*`. Every
 * backend module follows the `p_` + three letters shape, which makes the
 * alias invertible.
 */
const MODULE_ALIAS = /^p([a-z]{3})$/;

/**
 * The scope a *dynamic* translation key needs, or `null` when the key needs
 * no extra load.
 *
 * Components that render a key handed to them from outside — the toolbar
 * headline, the action bar's save caption — cannot declare that scope in
 * `provideTranslocoScope`, because they do not know it at build time. The
 * previous answer was to pre-provide every scope any caller might pass, which
 * cost ~30 requests on every cold load. Resolving the scope from the key
 * instead loads exactly the one that is actually used.
 *
 * Keys that are plain text (`'hippo points'`) or belong to a global scope
 * return `null`, as do aliases that do not follow the module convention.
 */
export function scopeForTranslationKey(key: string): string | null {
    // Headline entries carry their interpolation params as `key$$a$$b`.
    const alias = key.split('$$')[0]?.split('.')[0];
    if (!alias || GLOBAL_SCOPE_ALIASES.has(alias)) {
        return null;
    }
    const module = MODULE_ALIAS.exec(alias);
    return module ? `p_${module[1]}` : null;
}
